import { t } from 'i18next'
import React from 'react'
import { z } from 'zod'

import { InvoicingSystem, PlanCode } from '../common/enums.js'
import { BasicViewPaths, CustomerDTO, SubscriptionClaimDTO, SubscriptionDTO } from '../common/types.js'
import { CountryAuthMethodsResponse } from '../server/api/v1/country-auth-methods/handler.js'
import { DebugResponse } from '../server/api/v1/debug/types.js'
import { LocaleResponse } from '../server/api/v1/locales/types.js'
import { PortalCheckResponse } from '../server/api/v1/portal/auth/check/handler.js'
import { CustomerInvoicesResponse } from '../server/api/v1/portal/customer/invoices/handler.js'
import { PortalPaymentStatusResponse } from '../server/api/v1/portal/customer/payment-status/handler.js'
import { PortalProfileResponse } from '../server/api/v1/portal/customer/profile/handler.js'
import { changeLocale } from './i18n.js'
import { Customer } from './models/customer.js'
import { Invoice } from './models/invoice.js'
import { createLisaAndProducts } from './models/subscription-lisa-and-product.js'
import { Subscription } from './models/subscription.js'
import { parseRoute } from './props/parse-route.js'
import { Api, ErrorResponse, FormErrorMap, RedirectResponse, Route, UnknownResponse, View } from './types.js'
import { identify } from './utils/analytics.js'
import { formatLocaleForI18N, toBase64 } from './utils/utils.js'
import { UpsellOptionType } from './views/portal/cancel/cancel.js'

export const getCookie = (name: string): string | undefined => {
    const value = '; ' + document.cookie
    let parts = value.split('; ' + name + '=')

    if (parts.length === 0) {
        parts = value.split(name + '=')
    }

    if (parts.length === 2) {
        return parts.pop()?.split(';').shift()
    }

    return undefined
}

export const readJWT = (jwt: string) => {
    const tokenParts = jwt.split('.')
    const encodedPayload = tokenParts[1]
    const decodedPayload = atob(encodedPayload)

    return JSON.parse(decodedPayload)
}

const hasAuthExp = (response: unknown): response is { authExp: number } => {
    return (response as { authExp: number })?.authExp !== undefined
}

const isRedirectResponse = (response: unknown): response is RedirectResponse => {
    const hasRedirectUrl = (response as RedirectResponse)?.redirectUrl !== undefined
    const hasData = (response as RedirectResponse)?.data !== undefined

    return hasRedirectUrl || hasData
}

const hasError = (response: unknown): response is { error: string } => {
    return (response as { error: string })?.error !== undefined
}

const hasErrors = (response: unknown): response is { errors: { error: string }[] } => {
    return (response as { errors: string })?.errors !== undefined
}

const hasMessage = (response: unknown): response is { message: string } => {
    return (response as { message: string })?.message !== undefined
}

const isSuccess = (response: unknown): response is { success: true } => {
    return (response as { success: true })?.success === true
}

const hasSubscription = (response: unknown): response is { subscription: SubscriptionDTO } => {
    return !!(response as { subscription: SubscriptionDTO })?.subscription
}

const getCurrentRoute = (): Route => {
    const rawRoute = window.location.hash.substring(2).split('?')[0] // Remove '#/' prefix and strip query params

    return parseRoute(rawRoute)
}

const isSubscriptionResponse = (response: unknown): response is SubscriptionDTO => {
    return (response as SubscriptionDTO)?.basketId !== undefined
}

const parseValidationData = (issues: z.core.$ZodIssue[]): FormErrorMap => {
    return issues.reduce<FormErrorMap>((map, issue) => ((map[issue.path.join('')] = issue.code), map), {})
}

export const createApi = (view: View): Api => {
    const { apiBaseUrl } = view.state

    const handleFetchResponse = async (
        response: Response,
        callback?: (responseJson: unknown) => void,
    ): Promise<UnknownResponse> => {
        const responseJson = await response.json()

        // reset form errors
        view.state.formErrors = {}
        if (!responseJson || !response.ok) {
            if (hasError(responseJson)) {
                view.addNotification({
                    children: <p>{t(responseJson.error)}</p>,
                    isDefaultOpen: true,
                    duration: 4000,
                    type: 'error',
                })
            }

            if (hasErrors(responseJson)) {
                responseJson.errors.map((error) => {
                    view.addNotification({
                        children: <p>{t(error.error)}</p>,
                        isDefaultOpen: true,
                        duration: 4000,
                        type: 'error',
                    })
                })
            }

            if (responseJson) {
                const json = responseJson as ErrorResponse
                if (json?.data) {
                    view.state.formErrors = parseValidationData(json.data)
                } else if (hasMessage(responseJson)) {
                    view.addNotification({
                        children: <p>{t(responseJson.message)}</p>,
                        isDefaultOpen: true,
                        duration: 4000,
                        type: 'error',
                    })
                }
            }
        }

        if (response.status === 401) {
            window.location.hash = `#/${BasicViewPaths.AUTH_ERROR}`
        }

        if (hasAuthExp(responseJson)) {
            view.state.authExp = responseJson.authExp
            view.update()
        }

        if (callback) {
            callback(responseJson)
        }

        return {
            ...(responseJson as object),
            statusCode: response.status,
        }
    }

    const handleRedirect = (responseJson: unknown) => {
        if (isRedirectResponse(responseJson)) {
            if (responseJson.redirectUrl) {
                window.location.href = responseJson.redirectUrl
            }
        }
    }

    const api: Api = {
        fraudProtection: async () => {
            return await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${view.state.subscription?.uuid}/fraud-check`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(view.state.formData),
                }),
            )
        },
        upgrade: async (parentSubscriptionUuid) => {
            return await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${view.state.subscription?.uuid}/upgrade`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify({
                        parentSubscription: parentSubscriptionUuid,
                    }),
                }),
            )
        },
        fetchForMockedSubscription: async (subscriptionUuid, status) => {
            const result = await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/mock?status=${status}`)
            view.api.loadSubscription()

            return result
        },
        auth: async () => {
            if (!view.state.subscription) return
            view.state.isLoading = true
            view.update()

            await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${view.state.subscription?.uuid}/auth`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(view.state.formData),
                }),
                handleRedirect,
            )

            view.state.isLoading = false
            view.update()
        },
        sign: async () => {
            if (!view.state.subscription) return
            view.state.isLoading = true
            view.update()

            const subscriptionUuid = view.state.subscription.uuid

            await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/sign`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(view.state.formData),
                }),
                handleRedirect,
            )

            view.state.isLoading = false
            view.update()
        },
        creditCheck: async () => {
            if (!view.state.subscription) return
            view.state.isLoading = true
            view.update()

            const subscriptionUuid = view.state.subscription.uuid

            const response = await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/creditCheck`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(view.state.formData),
                }),
                handleRedirect,
            )

            if (isSuccess(response)) {
                view.state.creditCheckPassed = true
            }

            if (hasSubscription(response)) {
                const { subscription } = response
                const customer = subscription.customer

                let products = undefined
                if (subscription.invoicingSystem === InvoicingSystem.LISAAND) {
                    products = await createLisaAndProducts(subscription)
                }

                view.state.subscription = new Subscription(subscription, products)
                view.state.formData = {
                    ...view.state.formData,
                    firstName: customer?.firstName || '',
                    lastName: customer?.lastName || '',
                    email: customer?.email || '',
                    phone: customer?.phone || '',
                    postalCode: customer?.postalCode || '',
                    address: customer?.address || '',
                    city: customer?.city || '',
                    // TEMP: Lock shipping if defined
                    postalCodeLock: !view.state.subscription.customer?.canEditPostalCode,
                    addressLock: !view.state.subscription.customer?.canEditAddress,
                    cityLock: !view.state.subscription.customer?.canEditCity,
                }
            }

            view.state.isLoading = false
            view.update()
        },
        getSubscription: async (subscriptionUuid: string, currentView?: string) => {
            const subscription = await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/raw?view=${currentView}`, {
                    credentials: 'include',
                }),
            )

            let products = undefined
            if (isSubscriptionResponse(subscription)) {
                if (subscription.invoicingSystem === InvoicingSystem.LISAAND) {
                    products = await createLisaAndProducts(subscription)
                }
            }

            return {
                subscription,
                products,
            }
        },
        pollValidationStatus: async () => {
            if (!view.state.validation.isChecking && view.state.subscription) {
                view.state.validation.isChecking = true
                view.update()

                const subscriptionUuid = view.state.subscription.uuid

                try {
                    await handleFetchResponse(
                        await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/validation-status`, {
                            method: 'GET',
                            credentials: 'include',
                        }),
                        (responseJson) => {
                            view.state.validation.isChecking = false
                            view.update()
                            if (isRedirectResponse(responseJson)) {
                                if (responseJson.redirectUrl) {
                                    window.location.href = responseJson.redirectUrl
                                    view.api.loadSubscription()
                                    return
                                }
                            }

                            if (view.state.validation.timeout) {
                                window.clearTimeout(view.state.validation.timeout)
                            }

                            view.state.validation.timeout = window.setTimeout(() => {
                                void view.api.pollValidationStatus()
                            }, 5000)
                        },
                    )
                } catch (error) {
                    if (view.state.validation.timeout) {
                        window.clearTimeout(view.state.validation.timeout)
                    }

                    view.state.validation.timeout = window.setTimeout(() => {
                        void view.api.pollValidationStatus()
                    }, 5000)
                }
            }
        },
        loadSubscription: (subscriptionUuid?: string, callback?: () => void) => {
            const route = getCurrentRoute()
            if (!subscriptionUuid && route.type === 'basic' && route.subscriptionUuid) {
                subscriptionUuid = route.subscriptionUuid
            }

            if (subscriptionUuid) {
                view.state.isLoading = true
                view.update()

                void api
                    .getSubscription(subscriptionUuid, route.view)
                    .then((input) => {
                        const rawSubscription = input.subscription as unknown as SubscriptionDTO

                        if (view.state.debugOptions?.language) {
                            void changeLocale(view.state.debugOptions?.language)
                        } else {
                            if (rawSubscription?.locale) {
                                void changeLocale(rawSubscription.locale.code)
                            }
                        }

                        if (rawSubscription?.locale?.code) {
                            document.documentElement.lang = formatLocaleForI18N(rawSubscription.locale.code)
                        }

                        view.state.products = input.products
                        const selectedPlan = String(rawSubscription?.products?.[0]?.planPrice?.id || '')
                        if (selectedPlan) {
                            view.state.selectedPlan = selectedPlan
                            view.state.selectedPlans.push(selectedPlan)
                        }

                        view.updateSubscription(rawSubscription, false, input.products)
                    })
                    .finally(() => {
                        view.state.isLoading = false

                        view.update()
                        if (callback) {
                            callback()
                        }
                    })
            }
        },
        setSubscriptionAddOn: async (subscriptionUuid: string, addOnUuid: string, checked: boolean) => {
            view.state.isLoading = true
            view.update()

            await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/portal/customer/subscriptions/${subscriptionUuid}/add-on`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ addOnUuid, checked }),
                }),
                (jsonResponse) => {
                    if (isSubscriptionResponse(jsonResponse)) {
                        view.updateSubscription(jsonResponse, undefined, view.state.products)
                    } else {
                        view.state.isLoading = false
                        view.update()
                    }
                },
            )
        },
        setSubscriptionPlan: async (subscriptionUuid: string, planPriceId: string) => {
            view.state.isLoading = true
            view.update()

            await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/plan`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ planPriceId }),
                }),
                (jsonResponse) => {
                    if (isSubscriptionResponse(jsonResponse)) {
                        view.updateSubscription(jsonResponse, undefined, view.state.products)
                    } else {
                        view.state.isLoading = false
                        view.update()
                    }
                },
            )
        },
        selectSubscriptionPlan: async () => {
            const subscriptionUuid = view.state.subscription?.uuid || ''
            const planPriceId = view.state.subscription?.currentPlan?.id || ''
            await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/plan-select`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ planPriceId }),
            })
        },
        startLisaAndFlow: async (subscriptionUuid: string) => {
            view.state.isLoading = true
            view.update()

            await handleFetchResponse(
                await fetch(`${apiBaseUrl}/v1/subscriptions/${subscriptionUuid}/start-lisa`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                }),
                (responseJson: unknown) => {
                    if (isRedirectResponse(responseJson)) {
                        if (responseJson.redirectUrl) {
                            const url = new URL(responseJson.redirectUrl)
                            const consentId = window.CookieConsent?.getCookie('consentId') || ''

                            if (consentId) {
                                url.searchParams.set('consentId', consentId)
                            }

                            window.location.href = url.toString()
                        }
                    }
                },
            )

            view.state.isLoading = false
            view.update()
        },
        portal: {
            refreshToken: async () => {
                await handleFetchResponse(
                    await fetch(`${apiBaseUrl}/v1/portal/auth/refresh`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'content-type': 'application/json',
                        },
                    }),
                    () => {
                        view.update()
                    },
                )
            },
            login: async () => {
                view.state.isLoading = true
                view.update()

                await handleFetchResponse(
                    await fetch(`${apiBaseUrl}/v1/portal/auth`, {
                        method: 'POST',
                        headers: {
                            'content-type': 'application/json',
                        },
                        body: JSON.stringify(view.state.formData),
                    }),
                    handleRedirect,
                )

                view.state.isLoading = false
                view.update()
            },
            logout: async () => {
                view.state.isLoading = true
                view.update()

                await handleFetchResponse(
                    await fetch(`${apiBaseUrl}/v1/portal/auth/logout`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'content-type': 'application/json',
                        },
                    }),
                    handleRedirect,
                )

                view.state.isLoading = false
                view.update()
            },
            checkAuth: async () => {
                view.state.portal.authCheckStarted = true
                view.state.isLoading = true
                // view.update() This causes a bug where checkResponse is not awaited

                const checkResponse = await fetch(`${apiBaseUrl}/v1/portal/auth/check`, {
                    method: 'GET',
                    credentials: 'include',
                })

                if (checkResponse.ok) {
                    const { customer, locale, authExp, billing } = (await checkResponse.json()) as PortalCheckResponse

                    if (view.state.debugOptions?.language) {
                        void changeLocale(view.state.debugOptions?.language)
                    } else {
                        void changeLocale(locale)
                    }

                    view.state.portal.customer = new Customer(customer as CustomerDTO)
                    // loading required things for dashboard
                    // we don't need to wait for them to finish
                    view.state.authExp = authExp
                    view.state.portal.billing = billing
                    void view.api.portal.loadPaymentStatus()
                    void view.api.portal.loadCustomerSubscriptions()
                } else {
                    if (window.location.hash) {
                        localStorage.setItem('redirectToUrlAfterLogin', window.location.hash)
                    }
                }

                view.state.isLoading = false
                view.update()
            },
            changeSubscriptionProductNickname: async (subscriptionUuid, subscriptionProductUuid, nickname) => {
                const response = await fetch(`${apiBaseUrl}/v1/portal/customer/subscriptions/nickname`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify({
                        subscriptionUuid,
                        subscriptionProductUuid,
                        nickname,
                    }),
                })

                if (!response.ok) {
                    view.addNotification({
                        children: <p>{t('errors.nicknameChangeError')}</p>,
                        isDefaultOpen: true,
                        duration: 4000,
                        type: 'error',
                    })
                }

                return response.ok
            },
            loadCustomerSubscriptions: async () => {
                view.state.isLoading = true
                view.state.portal.subscriptions = []
                view.update()

                const response = await fetch(`${apiBaseUrl}/v1/portal/customer/subscriptions`, {
                    method: 'GET',
                    credentials: 'include',
                })

                if (response.ok) {
                    const responseJson = (await response.json()) as {
                        subscriptions: SubscriptionDTO[]
                    }

                    view.state.portal.subscriptions = responseJson.subscriptions.map((dtoSubscription) => {
                        const subscription = new Subscription(dtoSubscription)

                        subscription.products?.forEach((product) => {
                            product.customer = view.state.portal.customer
                        })

                        return subscription
                    })
                }

                view.state.isLoading = false
                view.update()

                try {
                    const customer = view.state.portal.customer
                    const subs = view.state.portal.subscriptions
                    if (customer && subs.length > 0) {
                        identify({
                            uuid: customer.uuid,
                            country: subs[0]?.country?.code || '',
                            subscriptions: subs.map((s) => ({
                                status: s.status,
                                plan: s.currentPlan.code === PlanCode.VALUE ? 'fixed' : 'flex',
                                device: s.mainProduct?.sku || s.products?.[0]?.sku || '',
                            })),
                        })
                    }
                } catch (error) {
                    console.error(error)
                }
            },
            loadCustomerInvoices: async () => {
                view.state.isLoading = true
                view.state.portal.invoices = []
                view.update()

                const response = await fetch(`${apiBaseUrl}/v1/portal/customer/invoices`, {
                    method: 'GET',
                    credentials: 'include',
                })

                if (response.ok) {
                    const responseJson = (await response.json()) as CustomerInvoicesResponse

                    view.state.portal.invoices = responseJson.invoices.map((invoice) => new Invoice(invoice))
                }

                view.state.isLoading = false
                view.update()
            },
            loadProfile: async () => {
                view.state.isLoading = true
                view.update()

                const checkResponse = await fetch(`${apiBaseUrl}/v1/portal/customer/profile`, {
                    method: 'GET',
                    credentials: 'include',
                })

                if (checkResponse.ok) {
                    const { customer } = (await checkResponse.json()) as PortalProfileResponse
                    view.state.portal.customer = new Customer(customer as CustomerDTO)
                }

                view.state.isLoading = false
                view.update()
            },
            updateProfile: async (updateProfileData) => {
                if (!view.state.isLoading) {
                    view.state.isLoading = true
                    view.update()

                    const profileResponse = await fetch(`${apiBaseUrl}/v1/portal/customer/profile`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'content-type': 'application/json',
                        },
                        body: JSON.stringify(updateProfileData),
                    })

                    if (profileResponse.ok) {
                        const { customer } = (await profileResponse.json()) as PortalProfileResponse
                        view.state.portal.customer = new Customer(customer as CustomerDTO)
                    }

                    view.state.isLoading = false
                    view.update()
                }
            },
            loadPaymentStatus: async () => {
                const checkResponse = await fetch(`${apiBaseUrl}/v1/portal/customer/payment-status`, {
                    method: 'GET',
                    credentials: 'include',
                })

                if (checkResponse.ok) {
                    view.state.portal.paymentStatus = (await checkResponse.json()) as PortalPaymentStatusResponse
                    view.update()
                }
            },
            loadLocales: async () => {
                view.state.isLoading = true
                view.state.portal.languages = {
                    locales: [],
                }
                view.update()

                const response = await fetch(`${apiBaseUrl}/v1/locale`, {
                    method: 'GET',
                })

                if (response.ok) {
                    const responseJson = response.json()

                    view.state.portal.languages = (await responseJson) as LocaleResponse
                }

                view.state.isLoading = false
                view.update()
            },
            loadDebug: async () => {
                view.state.isLoading = true
                view.state.portal.debug = {
                    enabled: false,
                } as unknown as DebugResponse
                view.update()

                const response = await fetch(`${apiBaseUrl}/v1/debug`, {
                    method: 'GET',
                })

                if (response.ok) {
                    const responseJson = response.json()

                    view.state.portal.debug = (await responseJson) as DebugResponse
                }

                view.state.isLoading = false
                view.update()
            },
            loadAuthenticationMethods: async (countryId) => {
                view.state.isLoading = true
                view.state.portal.loginAuthMethods = []
                view.update()

                const response = await fetch(`${apiBaseUrl}/v1/countries/${countryId}/auth-methods`, {
                    method: 'GET',
                })

                if (response.ok) {
                    const responseJson = await response.json()

                    view.state.portal.loginAuthMethods = responseJson as CountryAuthMethodsResponse[]
                }

                view.state.isLoading = false
                view.update()
            },
            cancelSubscription: async (subscriptionUuid) => {
                if (!view.state.isLoading) {
                    view.state.isLoading = true
                    view.update()

                    const { subscriptionClaim, subscriptionClaimFiles } = view.state.portal
                    const frontSideImages = await Promise.all(
                        subscriptionClaimFiles.frontSideDamageFiles.map(async (file) => await toBase64(file)),
                    )
                    const backSideImages = await Promise.all(
                        subscriptionClaimFiles.backSideDamageFiles.map(async (file) => await toBase64(file)),
                    )

                    const transformedClaim = {
                        ...subscriptionClaim,
                        frontSideDamageFiles: frontSideImages.length ? frontSideImages : undefined,
                        backSideDamageFiles: backSideImages.length ? backSideImages : undefined,
                    }

                    const cancelRequest = await handleFetchResponse(
                        await fetch(`${apiBaseUrl}/v1/portal/customer/subscriptions/${subscriptionUuid}/cancel`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'content-type': 'application/json',
                            },
                            body: JSON.stringify(transformedClaim),
                        }),
                    )

                    if (cancelRequest.statusCode === 200) {
                        const createdClaim = cancelRequest.claim as SubscriptionClaimDTO

                        if (createdClaim?.redirectUrl) {
                            window.location.href = createdClaim.redirectUrl
                        } else {
                            view.state.isLoading = false
                            view.state.portal.upsellChoice = UpsellOptionType.SWAP
                            view.state.portal.subscriptionClaimFiles = {
                                frontSideDamageFiles: [],
                                backSideDamageFiles: [],
                            }
                            view.state.portal.subscriptionClaim = {
                                termsAndConditions: false,
                                deviceDamaged: false,
                                dateOfDamage: new Date().toISOString(),
                                frontSideDamage: false,
                                frontSideDamageFiles: [],
                                backSideDamage: false,
                                backSideDamageFiles: [],
                            }

                            if (createdClaim) {
                                view.state.portal.subscriptions?.map((subscription) => {
                                    subscription.products?.map((product) => {
                                        if (product.uuid === createdClaim.subscriptionProductUuid) {
                                            product.data.claims?.push(createdClaim)
                                        }

                                        return product
                                    })

                                    return subscription
                                })
                            }

                            window.location.href = `#/subscriptions/${subscriptionUuid}`
                        }
                    } else {
                        view.state.isLoading = false
                    }

                    view.update()
                }
            },
            swapSubscription: async (subscriptionUuid) => {
                if (!view.state.isLoading) {
                    view.state.isLoading = true
                    view.update()

                    const { subscriptionClaim, subscriptionClaimFiles } = view.state.portal
                    const frontSideImages = await Promise.all(
                        subscriptionClaimFiles.frontSideDamageFiles.map(async (file) => await toBase64(file)),
                    )
                    const backSideImages = await Promise.all(
                        subscriptionClaimFiles.backSideDamageFiles.map(async (file) => await toBase64(file)),
                    )

                    const transformedClaim = {
                        ...subscriptionClaim,
                        frontSideDamageFiles: frontSideImages.length ? frontSideImages : undefined,
                        backSideDamageFiles: backSideImages.length ? backSideImages : undefined,
                    }

                    const swapRequest = await handleFetchResponse(
                        await fetch(`${apiBaseUrl}/v1/portal/customer/subscriptions/${subscriptionUuid}/swap`, {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'content-type': 'application/json',
                            },
                            body: JSON.stringify(transformedClaim),
                        }),
                    )

                    if (swapRequest.statusCode === 200) {
                        view.state.portal.upsellChoice = UpsellOptionType.SWAP
                        view.state.portal.subscriptionClaimFiles = {
                            frontSideDamageFiles: [],
                            backSideDamageFiles: [],
                        }
                        view.state.portal.subscriptionClaim = {
                            termsAndConditions: false,
                            deviceDamaged: false,
                            dateOfDamage: new Date().toISOString(),
                            frontSideDamage: false,
                            frontSideDamageFiles: [],
                            backSideDamage: false,
                            backSideDamageFiles: [],
                        }

                        const createdClaim = swapRequest.subscriptionClaim as SubscriptionClaimDTO

                        view.state.portal.latestSubscriptionClaim = createdClaim
                        view.state.portal.subscriptions?.map((subscription) => {
                            subscription.products?.map((product) => {
                                if (product.uuid === createdClaim.subscriptionProductUuid) {
                                    product.data.claims?.push(createdClaim)
                                }

                                return product
                            })

                            return subscription
                        })

                        if (createdClaim.redirectUrl) {
                            window.location.href = createdClaim.redirectUrl
                        } else {
                            window.location.href = `#/subscriptions/${subscriptionUuid}/swap-receipt`
                        }
                    } else {
                        window.location.href = `#/subscriptions/${subscriptionUuid}/swap`
                    }

                    view.state.isLoading = false
                    view.update()
                }
            },

            updatePaymentMethod: async (tokenUuid?: string) => {
                await handleFetchResponse(
                    await fetch(`${apiBaseUrl}/v1/portal/customer/payment-method`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'content-type': 'application/json',
                        },
                        body: JSON.stringify({
                            tokenUuid,
                        }),
                    }),
                    handleRedirect,
                )
            },
        },
    }

    const route = getCurrentRoute()

    if (route.type === 'portal') {
        void api.portal.checkAuth()
    }

    return api
}
